import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import { isOwner } from '../../utils/trainerRoleUtils';
import '../../styles/SchoolData.css';

const SchoolData = ({ schoolId }) => {
    const { t } = useTranslation(['school', 'common']);
    const { trainer } = useContext(TrainerContext);
    const currentUserRole = trainer?.trainerSchoolRole;
    const userIsOwner = isOwner(currentUserRole);

    const [school, setSchool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: {
            country: '',
            city: '',
            postalCode: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: '',
        },
    });
    const [halls, setHalls] = useState([]);
    const [newHallName, setNewHallName] = useState('');
    const [showAddHallForm, setShowAddHallForm] = useState(false);
    const [editHallId, setEditHallId] = useState(null);
    const [editHallName, setEditHallName] = useState('');

    // Invoice data state (only for owners)
    const [invoiceData, setInvoiceData] = useState(null);
    const [invoiceEditMode, setInvoiceEditMode] = useState(false);
    const [invoiceFormData, setInvoiceFormData] = useState({
        countryCode: 'PL',
        companyName: '',
        nip: '',
        regon: '',
        street: '',
        buildingNumber: '',
        apartmentNumber: '',
        postalCode: '',
        city: '',
        country: 'Polska',
    });

    useEffect(() => {
        if (!schoolId) return;
        const token = localStorage.getItem('jwt');
        axios
            .get(`/api/v1/school/${schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setSchool(response.data);
                setFormData(response.data);
                setLoading(false);
            })
            .catch(() => {
                setError(t('school:data.fetchError'));
                setLoading(false);
            });

        axios
            .get(`/api/v1/school/${schoolId}/halls`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setHalls(response.data || []);
            })
            .catch(() => {
                setHalls([]);
            });
    }, [schoolId]);

    // Fetch invoice data for owners
    useEffect(() => {
        if (!schoolId || !userIsOwner) return;
        const token = localStorage.getItem('jwt');
        axios
            .get(`/api/v1/school/${schoolId}/invoice-data`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                if (response.status === 200 && response.data) {
                    setInvoiceData(response.data);
                    setInvoiceFormData(response.data);
                }
            })
            .catch(() => {
                // No invoice data yet - that's ok
                setInvoiceData(null);
            });
    }, [schoolId, userIsOwner]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            address: {
                ...prevData.address,
                [name]: value,
            },
        }));
    };

    const handleSubmit = () => {
        const token = localStorage.getItem('jwt');
        axios
            .patch(`/api/v1/school/${schoolId}`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                setSchool(formData);
                setEditMode(false);
                alert(t('school:data.updateSuccess'));
            })
            .catch(() => {
                setError(t('school:data.updateError'));
            });
    };

    const handleAddHall = () => {
        if (!newHallName.trim()) {
            setError(t('school:data.hallNameRequired'));
            return;
        }

        const token = localStorage.getItem('jwt');
        axios
            .post(`/api/v1/school/${schoolId}/halls`, { name: newHallName }, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setHalls([...halls, response.data]);
                setNewHallName('');
                setShowAddHallForm(false); // Hide form after successful add
                setError('');
            })
            .catch(() => {
                setError(t('school:data.hallAddError'));
            });
    };

    const handleCancelAddHall = () => {
        setNewHallName('');
        setShowAddHallForm(false);
        setError('');
    };

    const handleEditHall = (hallId) => {
        if (!hallId) return;
        const token = localStorage.getItem('jwt');
        axios
            .patch(`/api/v1/school/halls/${hallId}`, { name: editHallName }, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setHalls(halls.map(hall => hall.id === hallId ? response.data : hall));
                setEditHallId(null);
                setEditHallName('');
            })
            .catch(() => {
                setError(t('school:data.hallEditError'));
            });
    };

    const handleDeleteHall = (hallId) => {
        if (!hallId) return;
        const token = localStorage.getItem('jwt');
        axios
            .delete(`/api/v1/school/halls/${hallId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                setHalls(halls.filter(hall => hall.id !== hallId));
            })
            .catch(() => {
                setError(t('school:data.hallDeleteError'));
            });
    };

    // Invoice data handlers
    const handleInvoiceChange = (e) => {
        const { name, value } = e.target;
        setInvoiceFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleInvoiceSubmit = () => {
        const token = localStorage.getItem('jwt');
        axios
            .put(`/api/v1/school/${schoolId}/invoice-data`, invoiceFormData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setInvoiceData(response.data);
                setInvoiceEditMode(false);
                alert(t('school:data.invoiceSaveSuccess'));
            })
            .catch(() => {
                setError(t('school:data.invoiceSaveError'));
            });
    };

    const handleInvoiceCancel = () => {
        if (invoiceData) {
            setInvoiceFormData(invoiceData);
        } else {
            setInvoiceFormData({
                countryCode: 'PL',
                companyName: '',
                nip: '',
                regon: '',
                street: '',
                buildingNumber: '',
                apartmentNumber: '',
                postalCode: '',
                city: '',
                country: 'Polska',
            });
        }
        setInvoiceEditMode(false);
    };

    const handleInvoiceDelete = () => {
        if (!window.confirm(t('school:data.invoiceDeleteConfirm'))) return;
        const token = localStorage.getItem('jwt');
        axios
            .delete(`/api/v1/school/${schoolId}/invoice-data`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
                setInvoiceData(null);
                setInvoiceFormData({
                    countryCode: 'PL',
                    companyName: '',
                    nip: '',
                    regon: '',
                    street: '',
                    buildingNumber: '',
                    apartmentNumber: '',
                    postalCode: '',
                    city: '',
                    country: 'Polska',
                });
                alert(t('school:data.invoiceDeleteSuccess'));
            })
            .catch(() => {
                setError(t('school:data.invoiceDeleteError'));
            });
    };

    if (loading) return <div className="school-data__loading">{t('school:data.loading')}</div>;
    if (error) return <div className="school-data__error">{error}</div>;
    if (!school) return null;

    const {
        name: schoolName,
        email,
        phone,
        address: { country, city, postalCode, street, buildingNumber, apartmentNumber },
    } = formData;

    return (
        <div className="school-data">
            {/* Basic School Information Section */}
            <div className="school-data__section">
                <div className="school-data__section-header">
                    <h3 className="school-data__section-title">{t('school:data.schoolInfo')}</h3>
                    {editMode ? (
                        <div className="school-data__header-buttons">
                            <button className="school-data__btn school-data__btn--small school-data__btn--primary" onClick={handleSubmit}>
                                {t('common:save')}
                            </button>
                            <button className="school-data__btn school-data__btn--small school-data__btn--secondary" onClick={() => setEditMode(false)}>
                                {t('common:cancel')}
                            </button>
                        </div>
                    ) : (
                        <button className="school-data__btn school-data__btn--small school-data__btn--primary" onClick={() => setEditMode(true)}>
                            {t('common:edit')}
                        </button>
                    )}
                </div>

                <div className="school-data__row school-data__row--basic-info">
                    <div className="school-data__field-group school-data__field-group--wide">
                        <label className="school-data__label">{t('school:data.schoolName')}</label>
                        <input
                            className="school-data__input"
                            name="name"
                            type="text"
                            value={schoolName}
                            onChange={handleChange}
                            disabled={!editMode}
                            placeholder={t('school:data.schoolName')}
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:email')}</label>
                        <input
                            className="school-data__input"
                            name="email"
                            type="text"
                            value={email}
                            onChange={handleChange}
                            disabled={!editMode}
                            placeholder={t('school:data.schoolEmail')}
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:phone')}</label>
                        <input
                            className="school-data__input"
                            name="phone"
                            type="text"
                            value={phone}
                            onChange={handleChange}
                            disabled={!editMode}
                            placeholder={t('school:data.phoneNumber')}
                        />
                    </div>
                </div>
            </div>

            {/* Address Information Section */}
            <div className="school-data__section">
                <h3 className="school-data__section-title">{t('school:data.schoolAddress')}</h3>

                <div className="school-data__row school-data__row--address-info">
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:data.country')}</label>
                        <input
                            className="school-data__input"
                            name="country"
                            type="text"
                            value={country}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder={t('school:data.country')}
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:data.city')}</label>
                        <input
                            className="school-data__input"
                            name="city"
                            type="text"
                            value={city}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder={t('school:data.city')}
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:data.postalCode')}</label>
                        <input
                            className="school-data__input"
                            name="postalCode"
                            type="text"
                            value={postalCode}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder={t('school:data.postalCode')}
                        />
                    </div>
                </div>

                <div className="school-data__row school-data__row--address-info">
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:data.street')}</label>
                        <input
                            className="school-data__input"
                            name="street"
                            type="text"
                            value={street}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder={t('school:data.street')}
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:data.buildingNumber')}</label>
                        <input
                            className="school-data__input"
                            name="buildingNumber"
                            type="text"
                            value={buildingNumber}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder={t('school:data.buildingNumber')}
                        />
                    </div>
                    <div className="school-data__field-group">
                        <label className="school-data__label">{t('school:data.apartmentNumber')}</label>
                        <input
                            className="school-data__input"
                            name="apartmentNumber"
                            type="text"
                            value={apartmentNumber}
                            onChange={handleAddressChange}
                            disabled={!editMode}
                            placeholder={t('school:data.apartmentNumber')}
                        />
                    </div>
                </div>
            </div>

            {/* Halls Section */}
            <div className="school-data__section school-data__halls-section">
                <h3 className="school-data__section-title">{t('school:data.halls')}</h3>

                <div className="school-data__halls-grid">
                    {halls.map((hall) => (
                        <div key={hall.id} className="school-data__hall-card">
                            {editHallId === hall.id ? (
                                <div className="school-data__hall-edit-content">
                                    <input
                                        className="school-data__hall-edit-input"
                                        type="text"
                                        value={editHallName}
                                        onChange={(e) => setEditHallName(e.target.value)}
                                        placeholder={t('school:data.editHallPlaceholder')}
                                    />
                                    <div className="school-data__hall-edit-actions">
                                        <button
                                            className="school-data__btn school-data__btn--small school-data__btn--primary"
                                            onClick={() => handleEditHall(hall.id)}
                                        >
                                            {t('common:save')}
                                        </button>
                                        <button
                                            className="school-data__btn school-data__btn--small school-data__btn--secondary"
                                            onClick={() => setEditHallId(null)}
                                        >
                                            {t('common:cancel')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="school-data__hall-content">
                                    <div className="school-data__hall-name">{hall.name}</div>
                                    <div className="school-data__hall-actions">
                                        <div
                                            className="school-data__hall-icon school-data__hall-edit-icon"
                                            onClick={() => {
                                                if (hall.id) {
                                                    setEditHallId(hall.id);
                                                    setEditHallName(hall.name);
                                                } else {
                                                    console.error("Hall id is undefined");
                                                }
                                            }}
                                            title={t('common:edit')}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z"/>
                                            </svg>
                                        </div>
                                        <div
                                            className="school-data__hall-icon school-data__hall-delete-icon"
                                            onClick={() => handleDeleteHall(hall.id)}
                                            title={t('common:delete')}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z"/>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Add new hall card - conditional rendering */}
                    <div className="school-data__hall-card school-data__hall-card--add">
                        {!showAddHallForm ? (
                            /* Plus icon state */
                            <div className="school-data__add-hall-plus" onClick={() => setShowAddHallForm(true)}>
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                                </svg>
                            </div>
                        ) : (
                            /* Form state */
                            <div className="school-data__new-hall-content">
                                <input
                                    className="school-data__new-hall-input"
                                    type="text"
                                    value={newHallName}
                                    onChange={(e) => setNewHallName(e.target.value)}
                                    placeholder={t('school:data.hallNamePlaceholder')}
                                />
                                <div className="school-data__add-hall-buttons">
                                    <button
                                        className="school-data__btn school-data__btn--primary school-data__btn--add-hall"
                                        onClick={handleAddHall}
                                    >
                                        {t('school:data.addHall')}
                                    </button>
                                    <button
                                        className="school-data__btn school-data__btn--secondary school-data__btn--add-hall"
                                        onClick={handleCancelAddHall}
                                    >
                                        {t('common:cancel')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Invoice Data Section - Only for school owners */}
            {userIsOwner && (
                <div className="school-data__section school-data__invoice-section">
                    <div className="school-data__section-header">
                        <h3 className="school-data__section-title">{t('school:data.invoiceTitle')}</h3>
                        {invoiceEditMode ? (
                            <div className="school-data__header-buttons">
                                <button
                                    className="school-data__btn school-data__btn--small school-data__btn--primary"
                                    onClick={handleInvoiceSubmit}
                                >
                                    {t('common:save')}
                                </button>
                                <button
                                    className="school-data__btn school-data__btn--small school-data__btn--secondary"
                                    onClick={handleInvoiceCancel}
                                >
                                    {t('common:cancel')}
                                </button>
                            </div>
                        ) : (
                            <div className="school-data__header-buttons">
                                <button
                                    className="school-data__btn school-data__btn--small school-data__btn--primary"
                                    onClick={() => setInvoiceEditMode(true)}
                                >
                                    {invoiceData ? t('common:edit') : t('common:add')}
                                </button>
                                {invoiceData && (
                                    <button
                                        className="school-data__btn school-data__btn--small school-data__btn--danger"
                                        onClick={handleInvoiceDelete}
                                    >
                                        {t('common:delete')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {!invoiceData && !invoiceEditMode ? (
                        <div className="school-data__invoice-empty">
                            <div className="school-data__invoice-empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01V17h2v-1.99H8zm0-4V13h2v-1.99H8zm4 4V17h5v-1.99h-5zm0-4V13h5v-1.99h-5z"/>
                                </svg>
                            </div>
                            <p className="school-data__invoice-empty-text">
                                {t('school:data.noInvoiceData')}
                            </p>
                            <button
                                className="school-data__btn school-data__btn--primary"
                                onClick={() => setInvoiceEditMode(true)}
                            >
                                {t('school:data.addInvoiceData')}
                            </button>
                        </div>
                    ) : (
                        <div className="school-data__invoice-card">
                            {/* Company Info */}
                            <div className="school-data__invoice-group">
                                <h4 className="school-data__invoice-group-title">{t('school:data.companyData')}</h4>
                                <div className="school-data__row school-data__row--invoice">
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.companyNameRequired')}</label>
                                        <input
                                            className="school-data__input"
                                            name="companyName"
                                            type="text"
                                            value={invoiceFormData.companyName || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder={t('school:data.companyName')}
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">NIP</label>
                                        <input
                                            className="school-data__input"
                                            name="nip"
                                            type="text"
                                            value={invoiceFormData.nip || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="0000000000"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">REGON</label>
                                        <input
                                            className="school-data__input"
                                            name="regon"
                                            type="text"
                                            value={invoiceFormData.regon || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="000000000"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="school-data__invoice-group">
                                <h4 className="school-data__invoice-group-title">{t('school:data.invoiceAddress')}</h4>
                                <div className="school-data__row school-data__row--invoice">
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.streetRequired')}</label>
                                        <input
                                            className="school-data__input"
                                            name="street"
                                            type="text"
                                            value={invoiceFormData.street || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder={t('school:data.street')}
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.buildingNumberShort')}</label>
                                        <input
                                            className="school-data__input"
                                            name="buildingNumber"
                                            type="text"
                                            value={invoiceFormData.buildingNumber || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder={t('school:data.buildingNumberShort')}
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.apartmentNumberShort')}</label>
                                        <input
                                            className="school-data__input"
                                            name="apartmentNumber"
                                            type="text"
                                            value={invoiceFormData.apartmentNumber || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder={t('school:data.apartmentNumberShort')}
                                        />
                                    </div>
                                </div>
                                <div className="school-data__row school-data__row--invoice">
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.postalCodeRequired')}</label>
                                        <input
                                            className="school-data__input"
                                            name="postalCode"
                                            type="text"
                                            value={invoiceFormData.postalCode || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder="00-000"
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.cityRequired')}</label>
                                        <input
                                            className="school-data__input"
                                            name="city"
                                            type="text"
                                            value={invoiceFormData.city || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder={t('school:data.city')}
                                        />
                                    </div>
                                    <div className="school-data__field-group">
                                        <label className="school-data__label">{t('school:data.countryRequired')}</label>
                                        <input
                                            className="school-data__input"
                                            name="country"
                                            type="text"
                                            value={invoiceFormData.country || ''}
                                            onChange={handleInvoiceChange}
                                            disabled={!invoiceEditMode}
                                            placeholder={t('school:data.country')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SchoolData;